import { Form, QuestionView, FormStatus, QuestionType, QuestionOption, QuestionTypeMapper } from '@formrails/form-builder/data-access';
import { ApplicationTemplateDto } from '../dtos/Responses/applicationTemplateDto';
import { FormElementDto } from '../dtos/formElementDto';
import { SectionDto } from '../dtos/sectionDto';

/**
 * Mapper utility for converting between ApplicationTemplateDto and Form model
 * Handles the conversion from API DTO to domain model
 */
export class ApplicationTemplateMapper {
  /**
   * Checks if the DTO has valid optionGenerationRules configuration
   * @param dto The form element DTO
   * @returns true if optionGenerationRules exists and has at least one configured value
   */
  private static hasOptionGenerationRules(dto: FormElementDto): boolean {
    const rules = dto.optionGenerationRules;
    if (!rules) return false;

    // Check if any of the configuration values are present
    return !!(
      rules.SourceContainer ||
      rules.TextProperty ||
      rules.CustomOptionId ||
      rules.CustomOptionText
    );
  }

  /**
   * Converts a FormElementDto to a QuestionView
   * @param dto The form element DTO from the backend
   * @returns QuestionView ready for UI display
   */
  static formElementDtoToQuestionView(dto: FormElementDto): QuestionView {
    // Map API type string to QuestionType enum
    let questionType = QuestionTypeMapper.mapApiTypeToQuestionType(dto.type);

    // Check if this is a dropdown or checkbox with optionGenerationRules
    // Set hasDynamicOptions flag for UI display
    const supportsDynamicOptions = dto.type === 'Dropdown' || dto.type === 'Checkbox';
    const hasDynamicOptions = supportsDynamicOptions && this.hasOptionGenerationRules(dto);

    if (hasDynamicOptions) {
      console.log('ApplicationTemplateMapper:', dto.type, 'has optionGenerationRules, setting hasDynamicOptions=true:', dto.optionGenerationRules);
    }

    // Convert QuestionResponseOptionDto[] to QuestionOption[] preserving isCustomOption
    const options: QuestionOption[] = dto.options.map(opt => ({
      text: opt.text,
      isCustomOption: opt.isCustomOption || false,
      customValue: undefined
    }));

    return {
      id: dto.formElementId,
      labelText: dto.labelText,
      additionalInfo: dto.additionalInfo,
      type: dto.type,
      isRequired: dto.validators?.['required'] === 'true' || dto.validators?.['required'] === 'True',
      validators: dto.validators || {},
      tooltip: dto.tooltip || undefined,
      helperText: dto.helperText || undefined,
      placeholder: dto.placeholder || undefined,
      semanticTags: [],
      options: options,
      isSearchable: dto.isSearchable,
      deleted: false,
      lastUpdatedOn: '0001-01-01T00:00:00',
      lastUpdatedBy: null,
      // UI-specific properties
      showMenu: false,
      isEditing: false,
      questionType: questionType || undefined,
      order: dto.order,
      questionNumber: dto.displayNumber ? parseInt(dto.displayNumber, 10) : undefined,
      sectionId: dto.sectionId,
      // Dynamic Dropdown configuration - hasDynamicOptions is the UI flag
      hasDynamicOptions: hasDynamicOptions || undefined,
      // Dynamic Dropdown configuration fields (populated from optionGenerationRules if present)
      sourceContainer: dto.optionGenerationRules?.SourceContainer,
      textProperty: dto.optionGenerationRules?.TextProperty,
      customOptionId: dto.optionGenerationRules?.CustomOptionId,
      customOptionText: dto.optionGenerationRules?.CustomOptionText
    };
  }

  /**
   * Converts a SectionDto to a Section QuestionView
   * @param sectionDto The section DTO from the backend
   * @param order The display order for the section
   * @returns QuestionView representing a Section
   */
  static sectionDtoToQuestionView(sectionDto: SectionDto, order: number): QuestionView {
    return {
      id: sectionDto.id,
      labelText: sectionDto.title,
      additionalInfo: '',
      type: 'Section',
      isRequired: false,
      validators: {},
      tooltip: undefined,
      helperText: undefined,
      placeholder: undefined,
      semanticTags: [],
      options: [],
      deleted: false,
      lastUpdatedOn: '0001-01-01T00:00:00',
      lastUpdatedBy: null,
      // UI-specific properties
      showMenu: false,
      isEditing: false,
      questionType: QuestionType.Section,
      order: order,
      questionNumber: undefined, // Sections don't have question numbers
      sectionId: undefined // Sections don't belong to other sections
    };
  }

  /**
   * Converts an ApplicationTemplateDto (API response) to a Form model for UI display
   * @param dto The application template DTO from the backend
   * @returns Form model ready for UI display
   */
  static toForm(dto: ApplicationTemplateDto): Form {
    // Convert TemplateStatus to FormStatus
    const formStatus = this.mapTemplateStatusToFormStatus(dto.status);

    // Convert FormElementDto[] to QuestionView[]
    // Sort by order to maintain the correct sequence
    const formElements = dto.applicationTemplateFormElements || [];
    console.log('ApplicationTemplateMapper: Form elements count:', formElements.length);
    const sortedElements = [...formElements].sort((a, b) => a.order - b.order);
    const questions: QuestionView[] = sortedElements.map(element => {
      const questionView = this.formElementDtoToQuestionView(element);
      console.log('Mapped element to question:', element.formElementId, '->', questionView.id, questionView.labelText);
      return questionView;
    });

    // Map sections to Section question types
    // Each section should be placed at the same order as the first question with that section ID
    const sections = dto.sections || [];
    console.log('ApplicationTemplateMapper: Sections count:', sections.length);
    
    const sectionQuestions: QuestionView[] = sections.map(sectionDto => {
      // Find the first question with this section ID
      const firstQuestionWithSection = sortedElements.find(
        element => element.sectionId === sectionDto.id
      );
      
      // Calculate order: if a question exists with this section ID, place section at the same order
      // Otherwise, use the section's own order from the DTO
      const sectionOrder = firstQuestionWithSection 
        ? firstQuestionWithSection.order 
        : sectionDto.order;
      
      const sectionQuestion = this.sectionDtoToQuestionView(sectionDto, sectionOrder);
      console.log('Mapped section to question:', sectionDto.id, '->', sectionQuestion.id, sectionQuestion.labelText, 'at order', sectionOrder);
      return sectionQuestion;
    });

    // Combine sections and questions, then sort by order
    const allQuestions = [...sectionQuestions, ...questions].sort((a, b) => {
      const orderA = a.order ?? 0;
      const orderB = b.order ?? 0;
      return orderA - orderB;
    });

    console.log('ApplicationTemplateMapper: Total questions mapped (including sections):', allQuestions.length);

    // Determine if question numbers should be shown based on whether any element has a displayNumber
    const showQuestionNumbers = formElements.some(
      element => element.displayNumber !== null && element.displayNumber !== undefined
    );

    const form: Form = {
      id: dto.id,
      title: dto.name, // Map name to title
      description: dto.description || '',
      version: (dto as any).version ?? 0, // Version may not be in DTO, default to 0
      status: formStatus,
      showQuestionNumbers: showQuestionNumbers,
      questions: allQuestions,
      assessmentMatrixIds: dto.assessmentMatrixIds ?? [],
      promotion: dto.promotion,
      createdAt: undefined, // Not available in ApplicationTemplateDto
      updatedAt: dto.lastUpdatedOn ? new Date(dto.lastUpdatedOn) : undefined
    };

    console.log('ApplicationTemplateMapper: Form created with', form.questions.length, 'questions');
    return form;
  }

  /**
   * Maps TemplateStatus to FormStatus enum
   * @param status TemplateStatus from the DTO
   * @returns FormStatus enum value
   */
  private static mapTemplateStatusToFormStatus(status: string): FormStatus {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'published':
        return FormStatus.Published;
      case 'archived':
        return FormStatus.Archived;
      // 'approved' and 'pendingapproval' were retired when sign-off moved onto
      // the template's promotion; neither meant the form was live.
      case 'draft':
      default:
        return FormStatus.Draft;
    }
  }
}

